/**
 * ProjectList - Main project listing page
 * Stitch design system
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Typography, TextField, InputAdornment, Skeleton, Avatar, AvatarGroup, Tooltip, Dialog, FormControl, Select, MenuItem, Autocomplete, createFilterOptions } from '@mui/material';
import { StoplichtRij, fetchProjectStatussen } from '../../ui-components/ProjectStoplicht';
import { useNavigate } from 'react-router-dom';
import DeadlineOverview from './DeadlineOverview';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { toast } from 'react-hot-toast';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const statusColorMap = {
  'Lead': { bg: '#f3e8ff', text: '#635983' }, 'Offerte': { bg: '#dbeafe', text: '#2563eb' },
  'In progress': { bg: T.primaryContainer, text: T.primaryDim }, 'Afgerond': { bg: '#dcfce7', text: '#166534' },
  'On Hold': { bg: '#fef3c7', text: '#b45309' }, 'Gearchiveerd': { bg: T.surfaceLow, text: T.outline },
  'Geannuleerd': { bg: '#fee2e2', text: '#dc2626' },
};

const typeColors = {
  'Video': { bg: '#dbeafe', text: '#1d4ed8' }, 'Testimonial': { bg: '#fce7f3', text: '#be185d' },
  'Interview': { bg: '#dcfce7', text: '#166534' }, 'Animatiefilm': { bg: '#fee2e2', text: '#b91c1c' },
  'Registratie': { bg: '#fef3c7', text: '#b45309' }, 'Campagne': { bg: '#d1fae5', text: '#065f46' },
  'Podcast': { bg: '#cffafe', text: '#0e7490' }, 'default': { bg: T.surfaceLow, text: T.secondary },
};

const getInitials = (name) => { if (!name) return '?'; const p = name.split(' '); return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase(); };
const stringToColor = (str) => { if (!str) return T.secondary; let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h); return ['#3b82f6','#8b5cf6','#ec4899','#f59e0b','#10b981','#06b6d4','#6366f1','#ef4444'][Math.abs(h) % 8]; };

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody,
    '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
    '&.Mui-focused fieldset': { borderColor: T.primary },
  },
  '& .MuiInputLabel-root': { fontFamily: T.fontBody, fontSize: '0.82rem' },
};

/* ── Project Card ──────────────────────────────────────────────────────────── */
// Compacte lijstrij (zoals 'Overzicht per project') mét stoplicht-statussen.
function ProjectRijCompact({ project, onClick, stoplicht }) {
  const title = project.fields?.Projectnr_en_naam || project.fields?.Projectnaam || 'Project';
  const rawClient = project.fields?.KlantnaamString || project.fields?.Klantnaam || [];
  const client = (() => { const c = Array.isArray(rawClient) ? rawClient[0] : rawClient || ''; return String(c).startsWith('rec') ? '' : c; })();
  const status = project.fields?.Status || '';
  const plNames = project.fields?.['Projectleider (Volledige naam)'] || project.fields?.['Projectleider (Voornaam)'] || [];
  const pl = (Array.isArray(plNames) ? plNames : [plNames]).filter(Boolean).join(', ');
  const st = statusColorMap[status] || statusColorMap['In progress'];
  return (
    <Box onClick={onClick} sx={{
      display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1,
      bgcolor: T.surface, borderRadius: '0.6rem', cursor: 'pointer', minWidth: 0,
      border: '1px solid transparent', boxShadow: T.shadow,
      '&:hover': { borderColor: `${T.primary}25`, boxShadow: '0 4px 14px rgba(45,51,53,0.08)' },
    }}>
      <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 700, color: T.text, flexShrink: 1 }}>
        {title}
      </Typography>
      {client && <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline, maxWidth: 220 }}>{client}</Typography>}
      {status && status !== 'In progress' && (
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
          color: st.text, bgcolor: st.bg, px: 1, py: 0.2, borderRadius: '9999px', flexShrink: 0 }}>
          {status}
        </Typography>
      )}
      {pl && (
        <Typography component="span" noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700,
          color: T.secondary, bgcolor: T.surfaceLow, px: 1, py: 0.2, borderRadius: '9999px', flexShrink: 0, maxWidth: 170 }}>
          {pl}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <StoplichtRij status={stoplicht} />
    </Box>
  );
}

function ProjectCard({ project, onClick }) {
  const title = project.fields?.Projectnr_en_naam || project.fields?.Projectnaam || 'Project';
  const number = project.fields?.Projectnummer || '';
  const rawClient = project.fields?.KlantnaamString || project.fields?.Klantnaam || [];
  const client = (() => { const c = Array.isArray(rawClient) ? rawClient[0] : rawClient || ''; return String(c).startsWith('rec') ? '' : c; })();
  const type = project.fields?.Projecttype || '';
  const status = project.fields?.Status || '';
  const plNames = project.fields?.['Projectleider (Volledige naam)'] || project.fields?.['Projectleider (Voornaam)'] || [];
  const projectleider = Array.isArray(plNames) ? plNames : plNames ? [plNames] : [];
  const creativeNames = project.fields?.['Naam (from Creative)'] || [];
  const creative = Array.isArray(creativeNames) ? creativeNames : creativeNames ? [creativeNames] : [];
  const rawTaskCount = project.fields?.['Aantal taken'];
  const taskCount = Array.isArray(rawTaskCount) ? String(rawTaskCount[0] || '') : String(rawTaskCount || '');
  const rawDelCount = project.fields?.['Aantal deliverables'];
  const deliverableCount = Array.isArray(rawDelCount) ? String(rawDelCount[0] || '') : String(rawDelCount || '');
  // Meeting notes op de kaart (wens Joris 20 aug): notities + gekoppelde
  // gesprekken samen — dat is "hoe goed is dit project gedocumenteerd".
  const notesCount = Number(project.fields?.['Aantal notities'] || 0) + Number(project.fields?.['Aantal meetings'] || 0);
  const typeStyle = typeColors[type] || typeColors.default;
  const team = [...new Set([...projectleider, ...creative])].filter(Boolean);

  return (
    <Box onClick={onClick} sx={{
      bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, cursor: 'pointer',
      boxShadow: T.shadow, border: '1px solid transparent', minWidth: 0, overflow: 'hidden',
      transition: 'all 0.15s',
      '&:hover': {
        boxShadow: '0 8px 24px rgba(45,51,53,0.08)',
        borderColor: `${T.primary}20`,
        transform: 'translateY(-2px)',
      },
    }}>
      {/* Title + client */}
      <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', fontWeight: 700, color: T.text, mb: 0.25 }}>
        {title}
      </Typography>
      {client && <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline, mb: 1.5 }}>{client}</Typography>}

      {/* Badges */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
        {type && (
          <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, color: typeStyle.text, bgcolor: typeStyle.bg, px: 1, py: 0.25, borderRadius: '9999px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {type}
          </Typography>
        )}
        {status && status !== 'In progress' && (
          <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, color: (statusColorMap[status] || statusColorMap['In progress']).text, bgcolor: (statusColorMap[status] || statusColorMap['In progress']).bg, px: 1, py: 0.25, borderRadius: '9999px' }}>
            {status}
          </Typography>
        )}
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2.5, mb: 1.5 }}>
        {taskCount && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon name="task_alt" size={14} sx={{ color: T.emerald }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>{taskCount.replace('🗓️  ', '')}</Typography>
          </Box>
        )}
        {deliverableCount && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon name="movie" size={14} sx={{ color: '#8b5cf6' }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>{deliverableCount.replace('🎬  ', '')}</Typography>
          </Box>
        )}
        {notesCount > 0 && (
          <Tooltip title="Notities & gekoppelde meetings" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon name="speaker_notes" size={14} sx={{ color: '#0ea5e9' }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>{notesCount}</Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Team + project number */}
      {team.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: '0.62rem', fontWeight: 700, border: '2px solid #fff' } }}>
            {team.map((name, idx) => (
              <Tooltip key={idx} title={name} arrow slotProps={{ tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem' } }, arrow: { sx: { color: T.text, '&::before': { border: 'none' } } } }}>
                <Avatar sx={{ bgcolor: stringToColor(name) }}>{getInitials(name)}</Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: T.outlineVariant }}>#{number}</Typography>
        </Box>
      )}
    </Box>
  );
}

/* ── Create Dialog ─────────────────────────────────────────────────────────── */
// Zoeken op elk stuk van de naam ("techn" vindt "Balm Speciale Technieken"), en
// niet meer dan 50 treffers tonen — bij ~2000 klanten is de lijst anders traag.
const keuzeFilter = createFilterOptions({
  matchFrom: 'any', trim: true, limit: 50,
  stringify: (o) => o?.label || '',
});

// Lege namen weg en dubbele labels samenvoegen: Airtable levert allebei, en een
// optie zonder label breekt het filteren van MUI (die leest option.label).
const schoneKeuzes = (rijen) => {
  const gezien = new Set();
  return rijen.filter((r) => {
    const label = String(r?.label || '').trim();
    if (!r?.id || !label || gezien.has(label.toLowerCase())) return false;
    gezien.add(label.toLowerCase());
    return true;
  });
};

function CreateProjectDialog({ open, onClose, onCreated }) {
  const [projectnaam, setProjectnaam] = useState('');
  const [status, setStatus] = useState('Lead');
  const [clients, setClients] = useState([]);
  const [personen, setPersonen] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPL, setSelectedPL] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingData(true);
      Promise.all([
        authFetch(`${AI_ENGINE_URL}/api/v1/airtable/clients?with_projects=false`).then(r => r.json()).catch(() => ({ clients: [] })),
        authFetch(`${BACKEND_URL}/Api/personen`).then(r => r.json()).catch(() => ({ records: [] })),
      ]).then(([clientsData, personenData]) => {
        setClients(schoneKeuzes((clientsData.clients || []).map(c => ({ id: c.airtableId, label: c.name }))));
        setPersonen(schoneKeuzes((personenData.records || []).map(p => ({ id: p.id, label: p.fields?.['Volledige naam'] || p.fields?.Voornaam || '' }))));
        setLoadingData(false);
      });
    } else { setProjectnaam(''); setStatus('Lead'); setSelectedClient(null); setSelectedPL(null); }
  }, [open]);

  const handleCreate = async () => {
    if (!projectnaam.trim()) { toast.error('Projectnaam is verplicht'); return; }
    setSaving(true);
    try {
      const body = { projectnaam: projectnaam.trim(), status };
      if (selectedClient) body.klantnaam = [selectedClient.id];
      if (selectedPL) body.projectleider = [selectedPL.id];
      const resp = await authFetch(`${BACKEND_URL}/Api/projects`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json().catch(() => ({}));
      toast.success(`Project "${projectnaam}" aangemaakt`);
      // De lijst leest de spiegel; lukte het bijwerken daarvan niet, zeg dat dan
      // eerlijk in plaats van een project te tonen dat er nog niet in staat.
      if (data.mirrored === false) {
        toast('Staat in Airtable, maar de lijst loopt nog achter — ververs over een minuut.', { icon: '⏳' });
      }
      onClose();
      if (onCreated) onCreated({ plNaam: selectedPL?.label || '' });
    } catch (err) { toast.error(`Fout bij aanmaken: ${err.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '0.75rem', p: 0.5 } }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>Nieuw project</Typography>
          <Box onClick={onClose} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}><Icon name="close" size={20} sx={{ color: T.outline }} /></Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Projectnaam" value={projectnaam} onChange={(e) => setProjectnaam(e.target.value)} required fullWidth autoFocus size="small" sx={inputSx} />
          <FormControl size="small" fullWidth sx={inputSx}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>
              {['Lead', 'Offerte', 'In progress'].map(s => (
                <MenuItem key={s} value={s}>
                  <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: (statusColorMap[s] || {}).text, bgcolor: (statusColorMap[s] || {}).bg, px: 1, py: 0.2, borderRadius: '9999px' }}>{s}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete options={clients} value={selectedClient} onChange={(_, val) => setSelectedClient(val)} loading={loadingData} renderInput={(params) => <TextField {...params} label="Klant" size="small" sx={inputSx} />} isOptionEqualToValue={(o, v) => o.id === v.id} size="small"
            getOptionLabel={(o) => o?.label || ''} filterOptions={keuzeFilter} autoHighlight noOptionsText="Geen klant gevonden" />
          <Autocomplete options={personen} value={selectedPL} onChange={(_, val) => setSelectedPL(val)} loading={loadingData} renderInput={(params) => <TextField {...params} label="Projectleider" size="small" sx={inputSx} />} isOptionEqualToValue={(o, v) => o.id === v.id} size="small"
            getOptionLabel={(o) => o?.label || ''} filterOptions={keuzeFilter} autoHighlight noOptionsText="Geen medewerker gevonden" />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 3 }}>
          <Box onClick={onClose} sx={{ px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>
            Annuleren
          </Box>
          <Box
            onClick={!saving && projectnaam.trim() ? handleCreate : undefined}
            sx={{
              px: 2.5, py: 0.75, borderRadius: '0.5rem',
              cursor: !saving && projectnaam.trim() ? 'pointer' : 'default',
              background: !saving && projectnaam.trim() ? `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)` : T.surfaceHigh,
              color: !saving && projectnaam.trim() ? '#fff' : T.outlineVariant,
              fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
              boxShadow: !saving && projectnaam.trim() ? '0 4px 12px rgba(99,89,131,0.25)' : 'none',
              display: 'flex', alignItems: 'center', gap: 0.5,
              transition: 'all 0.2s',
              '&:hover': !saving && projectnaam.trim() ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } : {},
            }}
          >
            <Icon name="add" size={16} sx={{ color: 'inherit' }} />
            {saving ? 'Aanmaken…' : 'Aanmaken'}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

/* ── Skeleton ──────────────────────────────────────────────────────────────── */
function ProjectListSkeleton() {
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
      {[1, 2, 3, 4, 5, 6].map(i => (
        <Box key={i} sx={{ p: 2.5, borderRadius: '0.75rem', bgcolor: T.surface, boxShadow: T.shadow }}>
          <Skeleton variant="text" width="70%" height={22} />
          <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" width={70} height={18} sx={{ borderRadius: '9999px', mb: 1.5 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="circular" width={26} height={26} />
            <Skeleton variant="circular" width={26} height={26} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ── Main ──────────────────────────────────────────────────────────────────── */
function ProjectList() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [serverHits, setServerHits] = useState([]);
  // Open Gripp-offertes zonder Airtable-record — draaien mee in Offertes & leads.
  const [grippOffers, setGrippOffers] = useState(null);
  const [leadBezig, setLeadBezig] = useState(null); // offertenummer dat wordt aangemaakt
  const zoekTimer = useRef(null);
  const zoekSeq = useRef(0);
  const [viewMode, setViewMode] = useState('mine');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [plFilter, setPlFilter] = useState(''); // '' = alle projectleiders
  // Cards ⇄ lijst (compact, met stoplicht-statussen) — voorkeur onthouden.
  const [weergave, setWeergave] = useState(() => {
    try { return localStorage.getItem('projectenWeergave') === 'lijst' ? 'lijst' : 'cards'; }
    catch { return 'cards'; }
  });
  const kiesWeergave = (w) => {
    setWeergave(w);
    try { localStorage.setItem('projectenWeergave', w); } catch { /* prima */ }
  };
  const [stoplichten, setStoplichten] = useState({});
  const stoplichtBezig = useRef(new Set());
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const userEmail = accounts?.[0]?.username || '';
  const userName = accounts?.[0]?.name || '';
  const userFirstName = userName.split(' ')[0]?.toLowerCase() || '';

  const loadProjects = async (withArchived = includeArchived) => {
    setLoading(true); setError(null);
    try {
      const url = withArchived ? `${BACKEND_URL}/Api/projects?includeArchived=true` : `${BACKEND_URL}/Api/projects`;
      const r = await authFetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setProjects(d.projects || d.records || []);
    } catch (err) { setError(`Kon projecten niet laden: ${err.message}`); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProjects(); }, []);
  useEffect(() => { loadProjects(includeArchived); }, [includeArchived]);

  // Open Gripp-offertes laden zodra de leads- of alles-tab opengaat.
  useEffect(() => {
    if ((viewMode !== 'leads' && viewMode !== 'alles') || grippOffers !== null) return;
    authFetch(`${BACKEND_URL}/Api/gripp-offertes/open`)
      .then((r) => (r.ok ? r.json() : { offers: [] }))
      .then((d) => setGrippOffers(d.offers || []))
      .catch(() => setGrippOffers([]));
  }, [viewMode, grippOffers]);

  // Server-zoek naast de client-filter: vindt draaidag-WERKNAMEN ("kadijk" →
  // 9677), leads/offertes buiten de geladen set én open Gripp-offertes die
  // (nog) geen project zijn. Best-effort: valt stil weg als de engine hapert.
  useEffect(() => {
    const q = searchTerm.trim();
    if (zoekTimer.current) clearTimeout(zoekTimer.current);
    const seq = ++zoekSeq.current; // trage oude response mag de actuele niet overschrijven
    if (q.length < 2) { setServerHits([]); return undefined; }
    zoekTimer.current = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/koppel-zoek?q=${encodeURIComponent(q)}`);
        if (r.ok && seq === zoekSeq.current) {
          const d = await r.json();
          if (seq === zoekSeq.current) setServerHits(d.opties || []);
        }
      } catch { /* zoeken is aanvullend, geen foutmelding waard */ }
    }, 350);
    return () => { if (zoekTimer.current) clearTimeout(zoekTimer.current); };
  }, [searchTerm]);

  const isUserProject = (project) => {
    if (!userFirstName) return false;
    const plNames = project.fields?.['Projectleider (Volledige naam)'] || project.fields?.['Projectleider (Voornaam)'] || [];
    return (Array.isArray(plNames) ? plNames : [plNames]).some(name => name && String(name).toLowerCase().includes(userFirstName));
  };

  // Status Lead/Offerte = nog geen project. De project-tabbladen en het
  // offerte-tabblad zijn strikt gescheiden; 'Alles' toont beide (verzoek 20 aug).
  const isLeadStatus = (p) => ['Lead', 'Offerte'].includes(p.fields?.Status);

  // PL-filter (26 aug): lookup kan array of string zijn — normaliseren.
  const plVan = (p) => {
    const raw = p.fields?.['Projectleider (Volledige naam)'] || p.fields?.['Projectleider (Voornaam)'] || [];
    return (Array.isArray(raw) ? raw : [raw]).filter(Boolean).map(String);
  };
  const matchtPl = (p) => !plFilter || plVan(p).includes(plFilter);

  const filteredProjects = useMemo(() => {
    let result = projects;
    if (viewMode === 'mine') result = result.filter(p => isUserProject(p) && !isLeadStatus(p));
    if (viewMode === 'all') result = result.filter(p => !isLeadStatus(p));
    if (viewMode === 'leads') result = result.filter(isLeadStatus);
    result = result.filter(matchtPl);
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(p => {
        const name = (p.fields?.Projectnr_en_naam || p.fields?.Projectnaam || '').toLowerCase();
        const num = (p.fields?.Projectnummer || '').toString().toLowerCase();
        const cn = p.fields?.KlantnaamString || p.fields?.Klantnaam || [];
        const cl = Array.isArray(cn) ? (cn[0] || '').toLowerCase() : cn.toLowerCase();
        // Projecttype is een Airtable multipleSelects en kan dus een array zijn.
      const tpRaw = p.fields?.Projecttype || '';
      const tp = (Array.isArray(tpRaw) ? tpRaw.join(', ') : String(tpRaw)).toLowerCase();
        return name.includes(s) || num.includes(s) || cl.includes(s) || tp.includes(s);
      });
    }
    return result;
  }, [projects, viewMode, searchTerm, userFirstName, plFilter]);

  // Stoplichten alleen in lijst-weergave laden (batch, gecached per sessie).
  useEffect(() => {
    if (weergave !== 'lijst' || !filteredProjects.length) return undefined;
    const timer = setTimeout(async () => {
      const nieuw = filteredProjects.map((p) => p.id)
        .filter((id) => stoplichten[id] === undefined && !stoplichtBezig.current.has(id));
      if (!nieuw.length) return;
      nieuw.forEach((id) => stoplichtBezig.current.add(id));
      const uit = await fetchProjectStatussen(nieuw);
      setStoplichten((prev) => ({ ...prev, ...uit }));
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weergave, filteredProjects]);

  // Tellers over dezelfde basis als wat je ziet: mét zoekfilter (test-feedback
  // 20 aug: 'nummers veranderen niet bij zoeken').
  const zoekBasis = useMemo(() => {
    const basis = projects.filter(matchtPl);
    if (!searchTerm) return basis;
    const s = searchTerm.toLowerCase();
    return basis.filter(p => {
      const name = (p.fields?.Projectnr_en_naam || p.fields?.Projectnaam || '').toLowerCase();
      const num = (p.fields?.Projectnummer || '').toString().toLowerCase();
      const cn = p.fields?.KlantnaamString || p.fields?.Klantnaam || [];
      const cl = Array.isArray(cn) ? (cn[0] || '').toLowerCase() : cn.toLowerCase();
      // Projecttype is een Airtable multipleSelects en kan dus een array zijn.
      const tpRaw = p.fields?.Projecttype || '';
      const tp = (Array.isArray(tpRaw) ? tpRaw.join(', ') : String(tpRaw)).toLowerCase();
      return name.includes(s) || num.includes(s) || cl.includes(s) || tp.includes(s);
    });
  }, [projects, searchTerm, plFilter]);
  const plOpties = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => plVan(p).forEach((n) => set.add(n)));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [projects]);

  const myProjectCount = useMemo(() => zoekBasis.filter(p => isUserProject(p) && !isLeadStatus(p)).length, [zoekBasis, userFirstName]);
  const projectCount = useMemo(() => zoekBasis.filter(p => !isLeadStatus(p)).length, [zoekBasis]);
  const leadCount = useMemo(() => zoekBasis.filter(isLeadStatus).length, [zoekBasis]);

  // Zoekresultaten van de server die niet al als kaart in beeld staan
  // (Gripp-offertes hebben sowieso geen kaart; leads/werknaam-hits soms wel).
  const extraHits = useMemo(() => {
    if (searchTerm.trim().length < 2) return [];
    const zichtbaar = new Set(filteredProjects.map(p => p.id));
    // Bewust GEEN tab-filter meer op de server-treffers: wie zoekt wil vinden
    // (test 20 aug: het enige kadijk-resultaat viel weg op de leads-tab). De
    // type-chip zegt al wat het is.
    return serverHits.filter(h => !zichtbaar.has(h.id));
  }, [serverHits, filteredProjects, searchTerm]);

  // Open Gripp-offerte als Airtable-lead aanmaken (26 aug): daarna draait hij
  // mee in de hele interne app. De kaart verhuist vanzelf naar de leads-lijst.
  const maakLead = async (e, o) => {
    e.stopPropagation();
    if (leadBezig) return;
    setLeadBezig(o.number);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/gripp-offertes/${encodeURIComponent(o.number)}/maak-lead`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409) { toast(`Offerte ${o.number} staat al in Airtable`); }
      else if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      else {
        toast.success(`Offerte ${o.number} aangemaakt als lead${d.klantGekoppeld ? '' : ' (klant niet gevonden — koppel handmatig)'}`);
      }
      setGrippOffers((prev) => (prev || []).filter((x) => x.number !== o.number));
      loadProjects();
    } catch (err) {
      toast.error(`Lead aanmaken mislukt: ${err.message}`);
    } finally {
      setLeadBezig(null);
    }
  };

  // Gripp-offertes die nog géén Airtable-record hebben (dedupe op nummer),
  // gefilterd op de zoekterm.
  const grippLijst = useMemo(() => {
    if (viewMode !== 'leads' && viewMode !== 'alles') return [];
    const bekend = new Set(projects.map((p) => String(p.fields?.Projectnummer || '')));
    const s = searchTerm.trim().toLowerCase();
    return (grippOffers || []).filter((o) => !bekend.has(o.number))
      .filter((o) => !s || `${o.number} ${o.subject} ${o.company}`.toLowerCase().includes(s));
  }, [grippOffers, projects, viewMode, searchTerm]);

  const VIEW_OPTIONS = [
    { key: 'mine', label: `Mijn projecten (${myProjectCount})`, icon: 'person' },
    { key: 'all', label: `Alle projecten (${projectCount})`, icon: 'groups' },
    { key: 'leads', label: `Offertes & leads (${leadCount}${grippOffers?.length ? ` + ${grippLijst.length || grippOffers.length} Gripp` : ''})`, icon: 'request_quote' },
    { key: 'alles', label: `Alles (${zoekBasis.length})`, icon: 'apps' },
    { key: 'deadlines', label: 'Mijn Deadlines', icon: 'event' },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      <PageHeader
        icon="folder_open"
        title="Projecten"
        subtitle={loading ? 'Laden…' : viewMode === 'mine'
          ? `${filteredProjects.length} van jouw projecten${includeArchived ? ' (incl. archief)' : ''}`
          : viewMode === 'leads'
            ? `${filteredProjects.length} offertes & leads`
            : viewMode === 'alles'
              ? `${filteredProjects.length} projecten + offertes/leads`
              : `${filteredProjects.length} ${includeArchived ? 'projecten (incl. archief)' : 'actieve projecten'}`}
        rightActions={(
          <>
            <Box onClick={() => setShowCreateDialog(true)} sx={{
              px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer',
              background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
              color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
              boxShadow: '0 4px 12px rgba(99,89,131,0.25)',
              display: 'flex', alignItems: 'center', gap: 0.5,
              transition: 'all 0.2s',
              '&:hover': { boxShadow: '0 6px 20px rgba(99,89,131,0.35)', transform: 'translateY(-1px)' },
            }}>
              <Icon name="add" size={16} sx={{ color: '#fff' }} /> Nieuw project
            </Box>
            <Tooltip title="Vernieuwen" arrow slotProps={{ tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem' } }, arrow: { sx: { color: T.text, '&::before': { border: 'none' } } } }}>
              <Box onClick={() => loadProjects()} sx={{ width: 36, height: 36, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
                <Icon name="refresh" size={18} sx={{ color: loading ? T.outlineVariant : T.outline }} />
              </Box>
            </Tooltip>
          </>
        )}
      />

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 4, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap' }}>
        {/* View mode pills */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {VIEW_OPTIONS.map(opt => (
            <Box key={opt.key} onClick={() => setViewMode(opt.key)} sx={{
              px: 1.5, py: 0.6, borderRadius: '9999px', cursor: 'pointer',
              bgcolor: viewMode === opt.key ? T.primaryContainer : T.surfaceLow,
              border: `1.5px solid ${viewMode === opt.key ? `${T.primary}40` : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.15s',
              '&:hover': viewMode !== opt.key ? { bgcolor: T.surfaceHigh } : {},
            }}>
              <Icon name={opt.icon} size={14} sx={{ color: viewMode === opt.key ? T.primary : T.outline }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: viewMode === opt.key ? 700 : 500, color: viewMode === opt.key ? T.primary : T.outline }}>
                {opt.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Archive toggle */}
        {viewMode !== 'deadlines' && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[{ key: false, label: 'Actief' }, { key: true, label: '+ Archief', icon: 'archive' }].map(opt => (
              <Box key={String(opt.key)} onClick={() => setIncludeArchived(opt.key)} sx={{
                px: 1.5, py: 0.6, borderRadius: '9999px', cursor: 'pointer',
                bgcolor: includeArchived === opt.key ? T.primaryContainer : T.surfaceLow,
                border: `1.5px solid ${includeArchived === opt.key ? `${T.primary}40` : 'transparent'}`,
                display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.15s',
                '&:hover': includeArchived !== opt.key ? { bgcolor: T.surfaceHigh } : {},
              }}>
                {opt.icon && <Icon name={opt.icon} size={14} sx={{ color: includeArchived === opt.key ? T.primary : T.outline }} />}
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: includeArchived === opt.key ? 700 : 500, color: includeArchived === opt.key ? T.primary : T.outline }}>
                  {opt.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Search */}
        {viewMode !== 'deadlines' && (
          <TextField
            fullWidth placeholder="Zoek op project, klant of type…"
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Icon name="search" size={18} sx={{ color: T.outlineVariant }} /></InputAdornment> }}
            sx={{ flex: 1, maxWidth: 420, ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], borderRadius: '9999px', bgcolor: T.surface } }}
          />
        )}

        {/* PL-filter — helemaal rechts, náást het zoekveld (26 aug: stond
            eerst tussen de archief-toggle en zoeken in, dat brak de groepering) */}
        {viewMode !== 'deadlines' && (
          <FormControl size="small" sx={{ minWidth: 132 }}>
            <Select displayEmpty value={plFilter} onChange={(e) => setPlFilter(e.target.value)}
              sx={{ borderRadius: '9999px', fontSize: '0.78rem', fontFamily: T.fontBody, bgcolor: T.surface,
                '& .MuiSelect-select': { py: 0.65 } }}>
              <MenuItem value="">Alle PL's</MenuItem>
              {plOpties.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {/* Cards ⇄ lijst */}
        <Box sx={{ display: 'flex', gap: 0.25, p: 0.35, bgcolor: T.surface, borderRadius: '9999px', boxShadow: T.shadow, ml: 0.5 }}>
          {[['cards', 'grid_view', 'Kaarten'], ['lijst', 'view_list', 'Lijst met statussen']].map(([w, icoon, tip]) => (
            <Tooltip key={w} title={tip} arrow>
              <Box onClick={() => kiesWeergave(w)} sx={{
                display: 'flex', alignItems: 'center', px: 1, py: 0.4, borderRadius: '9999px', cursor: 'pointer',
                bgcolor: weergave === w ? T.primaryContainer : 'transparent',
              }}>
                <Icon name={icoon} size={16} sx={{ color: weergave === w ? T.primary : T.outline }} />
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* Deadlines view */}
      {viewMode === 'deadlines' && <DeadlineOverview userEmail={userEmail} />}

      {/* Project views */}
      {viewMode !== 'deadlines' && (
        <>
          {error && (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: T.errorBg, borderRadius: '0.75rem', border: `1px solid ${T.error}20` }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.error, mb: 1 }}>{error}</Typography>
              <Typography onClick={() => loadProjects()} sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                Opnieuw proberen
              </Typography>
            </Box>
          )}

          {loading && !error && <ProjectListSkeleton />}

          {!loading && !error && filteredProjects.length === 0 && extraHits.length === 0 && (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Icon name="folder_open" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', fontWeight: 600, color: T.outline, mb: 0.5 }}>Geen projecten gevonden</Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant }}>
                {searchTerm ? 'Pas je zoekopdracht aan' : 'Er zijn geen actieve projecten'}
              </Typography>
            </Box>
          )}

          {!loading && !error && filteredProjects.length > 0 && (
            weergave === 'lijst' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredProjects.map(project => (
                  <ProjectRijCompact key={project.id} project={project} stoplicht={stoplichten[project.id]}
                    onClick={() => navigate(`/projects/${project.id}`)} />
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
                {filteredProjects.map(project => (
                  <ProjectCard key={project.id} project={project} onClick={() => navigate(`/projects/${project.id}`)} />
                ))}
              </Box>
            )
          )}

          {/* Open Gripp-offertes zonder Airtable-record — horen thuis in het
              offerte-overzicht ook al bestaat er (nog) geen lead-record. */}
          {!loading && !error && grippLijst.length > 0 && (
            <Box sx={{ mt: filteredProjects.length > 0 ? 4 : 0 }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                Open offertes in Gripp — nog geen project of lead in Airtable ({grippLijst.length})
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
                {grippLijst.map((o) => (
                  <Box key={o.number} onClick={() => navigate(`/offertes?mode=new&number=${o.number}`)} sx={{
                    p: 2, borderRadius: '0.75rem', bgcolor: T.surface, border: `1px solid ${T.outlineVariant}30`,
                    cursor: 'pointer', transition: 'all 0.15s',
                    '&:hover': { bgcolor: T.surfaceLow, borderColor: `${T.primary}40` },
                  }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 700, color: T.text }}>
                      {o.number}. {o.subject}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mt: 0.25 }}>
                      {[o.company, o.phase].filter(Boolean).join(' · ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.tertiary, fontWeight: 700 }}>
                        Gripp-offerte — klik voor de generator
                      </Typography>
                      <Typography component="span" role="button" variant="caption"
                        onClick={(e) => maakLead(e, o)}
                        sx={{ fontFamily: T.fontBody, fontWeight: 700, cursor: 'pointer',
                          color: leadBezig === o.number ? T.outlineVariant : T.primary,
                          px: 1, py: 0.25, borderRadius: '9999px', border: `1px solid ${T.primary}40`,
                          '&:hover': { bgcolor: `${T.primary}10` } }}>
                        {leadBezig === o.number ? 'Bezig…' : '➕ Lead in Airtable'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Server-zoek: hits buiten de kaarten hierboven — leads/offertes
              buiten de geladen set, Gripp-offertes zonder project, en projecten
              die alleen op hun draaidag-WERKNAAM matchen. */}
          {!loading && !error && extraHits.length > 0 && (
            <Box sx={{ mt: filteredProjects.length > 0 ? 4 : 0 }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                Ook gevonden — {viewMode === 'mine' ? 'álle projecten, offertes & leads (server-zoek kent geen eigenaar)' : 'offertes, leads & werknamen'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 720 }}>
                {extraHits.map(h => {
                  const offerteNr = h.type === 'offerte' && /^offer-(\d+)$/.test(h.id) ? h.id.slice(6) : null;
                  const klikbaar = h.type !== 'offerte' || !!offerteNr;
                  const typeLabel = h.type === 'offerte' ? 'Gripp-offerte → generator' : h.type === 'lead' ? 'Lead / offerte' : 'Project';
                  const typeIcon = h.type === 'offerte' ? 'request_quote' : h.type === 'lead' ? 'trending_up' : 'folder_open';
                  return (
                    <Box key={h.id} onClick={klikbaar ? () => navigate(offerteNr ? `/offertes?mode=new&number=${offerteNr}` : `/projects/${h.id}`) : undefined} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
                      borderRadius: '0.75rem', bgcolor: T.surface, border: `1px solid ${T.outlineVariant}30`,
                      cursor: klikbaar ? 'pointer' : 'default',
                      transition: 'all 0.15s',
                      ...(klikbaar ? { '&:hover': { bgcolor: T.surfaceLow, borderColor: `${T.primary}40` } } : {}),
                    }}>
                      <Icon name={typeIcon} size={18} sx={{ color: T.outline, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {h.label}
                        </Typography>
                        {h.sub && (
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {h.sub}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 700, color: h.type === 'offerte' ? T.tertiary : T.primary, bgcolor: h.type === 'offerte' ? `${T.tertiary}14` : T.primaryContainer, px: 1, py: 0.25, borderRadius: '9999px', flexShrink: 0 }}>
                        {typeLabel}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Een net aangemaakt project van een ándere projectleider valt buiten
          "Mijn projecten" — dan overschakelen naar Alle projecten, anders lijkt
          het aanmaken mislukt. */}
      <CreateProjectDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)}
        onCreated={({ plNaam } = {}) => {
          const vanMij = userFirstName && String(plNaam || '').toLowerCase().includes(userFirstName);
          if (viewMode === 'mine' && !vanMij) setViewMode('all');
          loadProjects();
        }} />
    </Box>
  );
}

export default ProjectList;
